import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import api from "@/lib/api";
import { KeyRound, CheckCircle, AlertCircle, Loader2, Eye, EyeOff, ArrowRight } from "lucide-react";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t("resetPassword.invalidLink"));
    }
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError(t("resetPassword.passwordsMismatch"));
      return;
    }

    if (newPassword.length < 8) {
      setError(t("resetPassword.passwordTooShort"));
      return;
    }

    setIsLoading(true);

    try {
      await api.post("/auth/password-reset/token", {
        token,
        new_password: newPassword
      });
      
      setSuccess(true);
      toast.success(t("resetPassword.successMessage"));
    } catch (err) {
      setError(err.response?.data?.detail || t("resetPassword.failedMessage"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
            success ? "bg-green-100" : "bg-primary/10"
          }`}>
            {success ? (
              <CheckCircle className="w-8 h-8 text-green-600" />
            ) : (
              <KeyRound className="w-8 h-8 text-primary" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {success ? t("resetPassword.completeTitle") : t("resetPassword.title")}
          </CardTitle>
          <CardDescription>
            {success 
              ? t("resetPassword.completeDescription") 
              : t("resetPassword.description")
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                {t("resetPassword.canSignIn")}
              </p>
              <Button onClick={() => navigate("/")} className="w-full" data-testid="reset-go-login">
                {t("resetPassword.goToSignIn")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">{t("resetPassword.newPassword")}</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("resetPassword.enterNewPassword")}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={!token}
                    data-testid="reset-new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1 h-8 w-8"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">{t("resetPassword.minChars")}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t("resetPassword.confirmPassword")}</Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("resetPassword.confirmNewPassword")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={!token}
                  data-testid="reset-confirm-password"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading || !token || !newPassword || !confirmPassword}
                data-testid="reset-submit"
              >
                {isLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("resetPassword.resetting")}</>
                ) : (
                  <>{t("resetPassword.resetButton")} <KeyRound className="w-4 h-4 ml-2" /></>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                {t("resetPassword.rememberPassword")}{" "}
                <Button 
                  type="button" 
                  variant="link" 
                  className="px-0 h-auto text-xs"
                  onClick={() => navigate("/")}
                >
                  {t("auth.signIn")}
                </Button>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
